/* eslint-disable no-unused-vars */
import React, {
  useState, createContext, useContext, useEffect,
} from 'react';
import endPoint from '../service/Service';
import SaveContext from './SaveContext';
import { compare as compareObject } from '../utils/functions';
import FlowDTO from '../dto/FlowDTO';

export const Context = createContext({
  bots: [],
  flow: {},
  flows: [],
  setActivedBot: () => { },
  deactivateBot: () => { },
  activeBotId: null,
  setFlow: () => { },
  fetchBots: (successCallback) => { },
  updateContextFlow: () => { },
  updateBackendFlow: () => { },
  publish: (sucessCallback, failureCallback) => { },
  lastPublication: {},
});

export const ContextProviderBot = ({ children }) => {
  const saveContext = useContext(SaveContext);

  const [activeBotId, setActiveBotId] = useState(null);
  const [fetchedBots, setFetchedBots] = useState({});
  const [bots, setBots] = useState();
  const [activeflow, setActiveFlow] = useState(null);
  const [flows, setFlows] = useState(null);
  const [lastPublication, setLastPublication] = useState(null);

  const checkIfAlreadyPublishDone = (checkIfPublishDoneUrl, sucessCallback) => {
    endPoint.get(checkIfPublishDoneUrl).then((responseCheck) => {
      if (responseCheck.status === 200) {
        const publishDone = responseCheck.data;
        if (publishDone.uiWebReady
          && publishDone.stackApiReady) {
          sucessCallback(publishDone);
        }
      }
    });
  };

  const fetchBots = (successCallback) => endPoint.get('/chatbots')
    .then((response) => {
      const _fetchedBots = response.data.data.reduce((_bots, item) => {
        const ret = { ..._bots };
        ret[item.id] = item;
        return ret;
      }, {});

      setFetchedBots(
        _fetchedBots,
      );

      setBots(
        Object.keys(_fetchedBots).reduce((_bots, item) => ({
          ..._bots,
          [item]: {
            id: _fetchedBots[item].id,
            name: _fetchedBots[item].name,
            description: _fetchedBots[item].description,
          },
        }), {}),
      );

      if (successCallback) successCallback();
    });

  function fetchedDetailedFlows(idChatbot, fetchedFlows) {
    const results = [];
    for (const fetchFlow of fetchedFlows) {
      results.push(endPoint.get(`/chatbots/${idChatbot}/flows/${fetchFlow.id}`).then((res) => {
        if (res.status === 200) {
          return res.data;
        }
      }));
    }
    Promise.all(results).then((res) => setFlows(res));
  }

  function setActivedBot(id) {
    setActiveBotId(id);
    endPoint.get(`/chatbots/${id}/flows`).then((response) => {
      if (response.status === 200) {
        fetchedDetailedFlows(id, response.data);
      }
    }, (fail) => null);
  }

  const deactivateBot = () => {
    setActiveFlow(null);
    setFlows(null);
  };

  const setFlow = (index = 0) => {
    if (flows == null) return null;
    const flowActived = new FlowDTO({ ...flows[index] }, fetchedBots[activeBotId]);
    setActiveFlow({ ...flowActived });
    setLastPublication(null);
    const checkIfPublishDoneUrl = `/chatbots/${activeBotId}/environments/${flowActived.environment.id}/deploys/latest/status`;
    checkIfAlreadyPublishDone(checkIfPublishDoneUrl, (published) => setLastPublication(published));
  };

  const updateContextFlow = (flowUpdated) => {
    setActiveFlow({ ...flowUpdated });
  };

  const updateBackendFlow = (flowUpdated, postUpdate) => {
    const { id } = flowUpdated;
    let _bots = flowUpdated.bots.map((b) => ({ ...b, engineRelease: b.engineRelease.id }));
    _bots = _bots.sort((a, b) => compareObject(a, b, 'order'));
    const data = {
      ...flowUpdated,
      stack: flowUpdated.stack.id,
      orchestrator: flowUpdated.orchestrator.id,
      bots: _bots,
    };

    saveContext.beginSaving();
    endPoint.put(`/chatbots/${activeBotId}/flows/${id}`, data)
      .then((response) => {
        if (response.status === 200) {
          const changedFlow = new FlowDTO(response.data, fetchedBots[activeBotId]);
          setActiveFlow(changedFlow);
          saveContext.finishSaving();
          if (postUpdate) postUpdate();
        }
      }, (error) => {
        setFlow();
        saveContext.setSavingStatus({
          ...saveContext,
          isSaving: false,
          isSuccessfulSave: false,
        });
        if (postUpdate) postUpdate();
      });
  };

  const publish = (sucessCallback, failureCallback) => {
    const data = { flowKey: activeflow.id };
    const publishUrl = `/chatbots/${activeBotId}/environments/${activeflow.environment.id}/deploys`;
    const checkIfPublishDoneUrl = `${publishUrl}/latest/status`;

    endPoint.post(publishUrl, data).then((response) => {
      if (response.status === 201) {
        const publicationTry = response.data;
        let count = 0;
        const checkIfPublishDoneLoop = setInterval(
          () => {
            if (count === 60) {
              clearInterval(checkIfPublishDoneLoop);
              if (failureCallback) failureCallback();
            } else {
              checkIfAlreadyPublishDone(
                checkIfPublishDoneUrl,
                (published) => {
                  if (published.id === publicationTry.deployId) {
                    sucessCallback();
                    setLastPublication(published);
                    clearInterval(checkIfPublishDoneLoop);
                  }
                },
              );
              count += 1;
            }
          },
          10000,
        );
      }
    }, (error) => {
      if (failureCallback) failureCallback();
    });
  };

  return (
    <Context.Provider value={{
      bots,
      activeBotId,
      setActivedBot,
      fetchBots,
      flow: activeflow,
      flows,
      deactivateBot,
      setFlow,
      updateContextFlow,
      updateBackendFlow,
      publish,
      lastPublication,
    }}
    >
      {children}
    </Context.Provider>
  );
};

export default Context;
